import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { HotToastService } from '@ngneat/hot-toast';
import { TestScoresOptionDto, UserAccountRegDto } from 'src/app/core/dtos/dto';
import { TestTypeEnum } from 'src/app/core/dtos/enum';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { LoadingService } from 'src/app/core/services/loading.service';
import { RegistrationManagementService } from 'src/app/core/services/registration-management.service';
import { RegistrationService } from 'src/app/core/services/registration.service';
import { ShoppingCartService } from 'src/app/core/services/shopping-cart.service';
import { ScoreReportOptionsSelectorComponent } from './score-report-options-selector/score-report-options-selector.component';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { BrandEnum } from 'src/app/core/dtos/enum';

@Component({
  selector: 'app-score-report-options',
  templateUrl: './score-report-options.component.html',
  styleUrls: ['./score-report-options.component.scss']
})
export class ScoreReportOptionsComponent implements OnInit {

  @ViewChild(ScoreReportOptionsSelectorComponent) optionSelector: ScoreReportOptionsSelectorComponent;

  testScoresOption: TestScoresOptionDto;
  regId: number;
  testTypeId: TestTypeEnum;
  isWebDelivery: boolean;
  userForm: FormGroup;
  regInfo: UserAccountRegDto;
  isAddedToCart: boolean;
  brandId: number;
  showIrccOnNewInstitutions: boolean;

  get f() { return this.userForm.controls; }

  constructor(
    private registrationService: RegistrationService,
    private route: ActivatedRoute,
    private toastService: HotToastService,
    private formBuilder: FormBuilder,
    private shoppingCartService: ShoppingCartService,
    private loadingService: LoadingService,
    private registrationManagementService: RegistrationManagementService,
    private configHelper: ConfigHelper
  ) { }

  ngOnInit(): void {
    this.regId = Number(this.route.snapshot.params.regid);
    this.testTypeId = this.route.snapshot.params.testtypeid;
    this.brandId = this.configHelper.getBrandId();

    this.userForm = this.formBuilder.group({
      formattedTestDateTime: [''],
      publicRegIdentifier: [''],
      testCentreName: [''],
      testCentreLocation: [''],
    });

    this.brandId = this.configHelper.getBrandId();
    this.getTestInfo();
    this.getTestScoreOptions();
  }

  setIrccStatus() {
    var irccInstitutionId = 1022;
    if (this.brandId === BrandEnum.CAEL) {
      irccInstitutionId = 1088;
    }
    for (const scoreRecipientReg of this.testScoresOption.scoreRecipientRegs) {
      if (scoreRecipientReg.scoreRecipients.some(sr => sr.id === irccInstitutionId)) {
        this.showIrccOnNewInstitutions = true;
        return;
      }
    }

    this.showIrccOnNewInstitutions = false;
  }

  getTestInfo() {
    const pipe = new DatePipe('en-US');
    this.registrationService.getTestInfo(this.regId).subscribe(result => {
      this.regInfo = result.content;
      this.testTypeId = Number(result.content.testTypeId);
      this.isWebDelivery = result.content.isWebDelivery;
      this.userForm.controls.testCentreName.setValue(result.content.testCentreName);
      this.userForm.controls.testCentreLocation.setValue(result.content.testCentreLocation);
      this.userForm.controls.publicRegIdentifier.setValue(result.content.publicRegIdentifier);
      this.userForm.controls.formattedTestDateTime.setValue(pipe.transform(result.content.testDateTime, "EEE MMM dd, yyyy"));
    },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        }
      });
  }

  getTestScoreOptions() {
    this.registrationService.getTestScoresOptions(this.regId).subscribe(
      result => {
        this.testScoresOption = result.content;
        this.setIrccStatus();
      },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        }
      });
  }

  saveChanges() {
    this.loadingService.show();
    if (!this.optionSelector.scoreRecipientsUpdated && this.optionSelector.shipmentPreferenceUpdated) {
      const scoreRecipientIdsForRegularMail = this.optionSelector.shippingPreferences.filter(x => x.productId === 1).map(o => o.institution.id);
      const scoreRecipientIdsForPriorityShipping = this.optionSelector.shippingPreferences.filter(x => x.price > 0).map(o => o.institution.id);
      this.updateShippmentPreference(scoreRecipientIdsForRegularMail, scoreRecipientIdsForPriorityShipping);
    }
    if (this.optionSelector.scoreRecipientsUpdated) {
      this.setTestScoresOptions();
    }
    this.loadingService.hide();
  }

  updateShippmentPreference(scoreRecipientIdsForRegularMail: number[], scoreRecipientIdsForPriorityShipping: number[]) {
    if (scoreRecipientIdsForRegularMail.length > 0) {
      this.addRegularMail(scoreRecipientIdsForRegularMail);
    }
    if (scoreRecipientIdsForPriorityShipping.length > 0) {
      this.addExpressServiceToCart(scoreRecipientIdsForPriorityShipping);
    }
  }

  addRegularMail(scoreRecipientRegIds: number[]) {
    this.registrationManagementService.AddScoreRecipientRegularMail(scoreRecipientRegIds).subscribe(
      () => {
        this.toastService.success("Score report mailing preference has been updated.", { duration: 5000 });
      },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        }
      });
  }

  setTestScoresOptions() {
    if (this.optionSelector.Validate()) {
      this.registrationService.setTestScoresOptions(this.optionSelector.testScoresOption).subscribe(
        () => {
          this.toastService.success("Score report recipients have been updated.", { duration: 5000 });
          if (this.optionSelector.shipmentPreferenceUpdated) {
            this.getScoreRecipientIdsForShippmentPreference();
          }
        },
        (error: HttpErrorResponse) => {
          if (error.status === 400) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
        });
    }
  }

  getScoreRecipientIdsForShippmentPreference() {
    // Filter by name because we don't know the ids of newly added score recipients
    const scoreRecipientNamesForRegularMail = this.optionSelector.shippingPreferences.filter(x => x.productId === 1).map(o => o.institution.institutionName);
    const scoreRecipientNamesForPriorityShipping = this.optionSelector.shippingPreferences.filter(x => x.price > 0).map(o => o.institution.institutionName);
    this.registrationService.getTestScoresOptions(this.regId).subscribe(
      result => {
        const scoreRecipientIdsForRegularMail = result.content.scoreRecipientRegs.filter(x => scoreRecipientNamesForRegularMail.includes(x.institutionName)).map(i => i.id);
        const scoreRecipientIdsForPriorityShipping = result.content.scoreRecipientRegs.filter(x => scoreRecipientNamesForPriorityShipping.includes(x.institutionName)).map(i => i.id);
        this.updateShippmentPreference(scoreRecipientIdsForRegularMail, scoreRecipientIdsForPriorityShipping);
      },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        }
      });
  }

  addExpressServiceToCart(scoreRecipientIds: number[]) {
    this.shoppingCartService.addExpressServiceToCart(scoreRecipientIds).subscribe(
      () => {
        this.isAddedToCart = true;
      },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        }
      });
  }

}

